// Shared helpers for the structural-edit applicator: zero-width range
// construction (for inserts), dominant-separator detection, and clause-tail
// scanning. Used by every per-op planner.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — add/remove.

import type { SqlRange } from '../../sql-grammar/model';
import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import { scanTopLevelKeywords, type TopKeyword } from '../structure/scanner';
import { trySkipNonCode, type SkipContext } from '../classify/tokenSkip';

import type { EditCtx } from './applyStructuralEdit';

export interface ListItem {
  readonly range: SqlRange;
}

// Build a zero-width PlannedByteEdit at `offset` (insert-only). The
// `replacement` will be spliced in at that offset by emitSource.
export function insertEdit(
  ctx: EditCtx,
  offset: number,
  replacement: string,
  rationale: string,
): { range: SqlRange; replacement: string; rationale: string } {
  return {
    range: rangeFromOffsets(ctx.lineIdx, offset, offset),
    replacement,
    rationale,
  };
}

// Build a delete (replacement = '') over `[start, end)`.
export function deleteEdit(
  ctx: EditCtx,
  start: number,
  end: number,
  rationale: string,
): { range: SqlRange; replacement: string; rationale: string } {
  return {
    range: rangeFromOffsets(ctx.lineIdx, start, end),
    replacement: '',
    rationale,
  };
}

// Build a replace over `[start, end)` with `replacement` (non-empty text and
// a non-zero-width range — distinct from insertEdit's zero-width and
// deleteEdit's empty replacement).
export function replaceEdit(
  ctx: EditCtx,
  start: number,
  end: number,
  replacement: string,
  rationale: string,
): { range: SqlRange; replacement: string; rationale: string } {
  return {
    range: rangeFromOffsets(ctx.lineIdx, start, end),
    replacement,
    rationale,
  };
}

// Detect the dominant comma-separator style by a MAJORITY VOTE over the
// inter-item slices of every adjacent pair (e.g. `, ` or `,\n    ` or
// `\n  , `), reused as the separator for inserts. The most frequent slice
// wins; ties resolve to the first-occurring style. Uniform-preserving — a
// list with one consistent separator yields that exact slice (unchanged from
// the old last-two sampling) — while mixed lists no longer lose fidelity to
// whatever the final pair happened to use. Falls back to `, ` for <2 items.
//
// F36: select-item ranges trim LEADING whitespace+comments but only TRAILING
// whitespace (not comments), so an item with a trailing line/block comment
// strands that comment inside the inter-item slice. Reusing the slice verbatim
// as the insert separator splices a stray `-- comment` (or `/* */`) into the
// emitted SQL — and a line comment used as a separator can also comment-out
// the tokens that follow it on the same line. We therefore strip comment spans
// from each slice before voting/reusing it, keeping only the structural comma
// + whitespace layout (e.g. `,\n  `).
export function detectListSeparator(source: string, items: readonly ListItem[]): string {
  if (items.length < 2) return ', ';
  const slices: string[] = [];
  for (let k = 0; k < items.length - 1; k++) {
    const raw = source.slice(items[k].range.endOffset, items[k + 1].range.startOffset);
    slices.push(stripCommentSpans(raw));
  }
  return majoritySlice(slices);
}

// Engine-agnostic comment stripper for separator slices. A separator slice is
// punctuation + whitespace + (possibly) comments — never a string/quoted-id —
// so we only remove comment spans and keep everything else verbatim. We reuse
// `trySkipNonCode` (the shared scanner helper) to find each comment's end
// rather than hand-rolling matchers for `--` / `/* */` / mysql `#`.
//
// `detectListSeparator`'s signature carries no ParseOptions/engine, so we run
// the stripper with hash-comments enabled unconditionally: `#` only opens a
// comment in MySQL, but a bare `#` is not valid punctuation inside a
// non-MySQL select-list separator either, so treating it as a comment here is
// safe across engines. String/bracket/backtick detection is left off — those
// never appear in a comma-separator slice — so `trySkipNonCode` only ever
// fires on a genuine comment opener here.
const SEPARATOR_SKIP_CTX: SkipContext = {
  supportsBacktick: false,
  supportsBracket: false,
  supportsDollar: false,
  supportsHashComment: true,
};

function stripCommentSpans(slice: string): string {
  let out = '';
  let i = 0;
  const end = slice.length;
  while (i < end) {
    const c = slice[i];
    const c2 = slice[i + 1] ?? '';
    const isComment = (c === '-' && c2 === '-') || (c === '/' && c2 === '*') || c === '#';
    if (isComment) {
      // Drop the horizontal whitespace that preceded the comment opener too,
      // so removing a trailing line comment yields `,\n  ` (not `, \n  `) and
      // removing an inline block comment yields `, ` (not `,  `). We trim only
      // spaces/tabs — never a newline or the comma — so the comma + indent
      // layout is preserved.
      out = out.replace(/[ \t]+$/, '');
      const next = trySkipNonCode(slice, i, end, SEPARATOR_SKIP_CTX);
      // `trySkipNonCode` returns -1 only when no skip applied; for these
      // openers it always advances, but guard defensively.
      i = next > i ? next : i + 1;
      continue;
    }
    out += c;
    i += 1;
  }
  return out;
}

// Return the most frequent string in `slices`; ties resolve to the one that
// occurs first. `slices` is guaranteed non-empty by callers.
function majoritySlice(slices: readonly string[]): string {
  const counts = new Map<string, number>();
  let best = slices[0];
  let bestCount = 0;
  for (const s of slices) {
    const n = (counts.get(s) ?? 0) + 1;
    counts.set(s, n);
    if (n > bestCount) {
      bestCount = n;
      best = s;
    }
  }
  return best;
}

// Top-level keyword scan over the statement byte range. Used to locate
// clause-keyword positions on demand (we don't pre-cache them on
// QueryStructure to keep the read-only surface minimal).
export function scanStatementKeywords(ctx: EditCtx): readonly TopKeyword[] {
  const { source, options, statementInfo } = ctx;
  return scanTopLevelKeywords(
    source,
    statementInfo.range.startOffset,
    statementInfo.range.endOffset,
    options,
  );
}

// Find the first top-level keyword (by upper-cased value) at or after `from`.
// Returns the keyword or null when not found.
export function findKeyword(
  kws: readonly TopKeyword[],
  upper: string,
  fromOffset = 0,
): TopKeyword | null {
  for (const k of kws) {
    if (k.start >= fromOffset && k.upper === upper) return k;
  }
  return null;
}

// Locate the byte offset where a new clause should be inserted, given the
// keyword stream. The position is just before the first of the named
// "boundary" keywords whose start is >= afterOffset, falling back to
// `fallbackEnd` when none of them is present.
export function findClauseInsertionPoint(
  kws: readonly TopKeyword[],
  afterOffset: number,
  boundaries: readonly string[],
  fallbackEnd: number,
): number {
  let earliest = fallbackEnd;
  for (const k of kws) {
    if (k.start < afterOffset) continue;
    if (boundaries.includes(k.upper)) {
      if (k.start < earliest) earliest = k.start;
    }
  }
  return earliest;
}

// Find the GROUP/ORDER keyword for a `BY`-paired clause; returns the GROUP/ORDER
// keyword (whose range covers GROUP or ORDER, NOT including BY) and the offset
// past the `BY` token, or null when the clause is absent. Used for "remove last
// group-by item" / "remove last order-by item" edits which must drop the
// clause-leading `GROUP BY` / `ORDER BY` along with the only item.
export function findByPairedClause(
  source: string,
  kws: readonly TopKeyword[],
  upper: 'GROUP' | 'ORDER',
): { keyword: TopKeyword; afterByOffset: number } | null {
  for (let k = 0; k < kws.length; k++) {
    if (kws[k].upper !== upper) continue;
    const next = kws[k + 1];
    if (next && next.upper === 'BY') {
      return { keyword: kws[k], afterByOffset: next.end };
    }
  }
  void source;
  return null;
}

// Find the byte offset just after the last byte of a clause that ends at
// the next clause-boundary keyword. `clauseStart` is the offset just after
// the clause's leading keyword bytes; `boundaries` are the upper-cased
// keywords that terminate this clause.
export function findClauseEnd(
  kws: readonly TopKeyword[],
  clauseStart: number,
  boundaries: readonly string[],
  fallbackEnd: number,
): number {
  for (const k of kws) {
    if (k.start <= clauseStart) continue;
    if (boundaries.includes(k.upper)) return k.start;
  }
  return fallbackEnd;
}

// Trim trailing whitespace from the bytes of `[start, end)` to the right.
export function trimTrailingWs(source: string, start: number, end: number): number {
  let e = end;
  while (e > start && /\s/.test(source[e - 1])) e--;
  return e;
}

// Trim leading whitespace from the bytes of `[start, end)` to the left.
export function trimLeadingWs(source: string, start: number, end: number): number {
  let s = start;
  while (s < end && /\s/.test(source[s])) s++;
  return s;
}

// Pick the byte offset where a synthesized clause should land. Take the
// minimum of the candidate "tail" anchor and the next clause boundary, then
// trim trailing whitespace so the inserted text lands just past the last
// non-whitespace byte (preserving the original separator that follows).
export function pickClauseInsertOffset(
  source: string,
  candidate: number,
  boundary: number,
  lowerBound: number,
): number {
  return trimTrailingWs(source, lowerBound, Math.min(candidate, boundary));
}

// Common boundary keyword sets reused by clause-edit planners.
export const CLAUSE_BOUNDARIES_AFTER_FROM: readonly string[] = [
  'WHERE', 'GROUP', 'HAVING', 'WINDOW', 'ORDER', 'LIMIT', 'OFFSET', 'FETCH',
  'UNION', 'INTERSECT', 'EXCEPT', 'RETURNING',
];

export const CLAUSE_BOUNDARIES_AFTER_WHERE: readonly string[] = [
  'GROUP', 'HAVING', 'WINDOW', 'ORDER', 'LIMIT', 'OFFSET', 'FETCH',
  'UNION', 'INTERSECT', 'EXCEPT', 'RETURNING',
];

export const CLAUSE_BOUNDARIES_AFTER_GROUP_BY: readonly string[] = [
  'HAVING', 'WINDOW', 'ORDER', 'LIMIT', 'OFFSET', 'FETCH',
  'UNION', 'INTERSECT', 'EXCEPT', 'RETURNING',
];

// HAVING sits after GROUP BY (extended precedence). A synthesized HAVING lands
// BEFORE WINDOW / ORDER BY / LIMIT / OFFSET / FETCH / set-op / RETURNING.
export const CLAUSE_BOUNDARIES_AFTER_HAVING: readonly string[] = [
  'WINDOW', 'ORDER', 'LIMIT', 'OFFSET', 'FETCH',
  'UNION', 'INTERSECT', 'EXCEPT', 'RETURNING',
];

export const CLAUSE_BOUNDARIES_AFTER_ORDER_BY: readonly string[] = [
  'LIMIT', 'OFFSET', 'FETCH',
  'UNION', 'INTERSECT', 'EXCEPT', 'RETURNING',
];
